import { PermissionCategory } from './types';

type BaseUIPattern = {
  name: string;
  description: string;
  tooltip?: {
    text: string; // i18n key
    placement?: 'top' | 'right' | 'bottom' | 'left';
  };
};

export type UIPattern = BaseUIPattern &
  (
    | {
        type: 'toggle';
        defaultValue: boolean;
      }
    | {
        type: 'dropdown';
        defaultValue: string;
        options: Array<{ value: string; label: string }>;
      }
  );

interface BasePermissionConfig {
  key: string;
  category: PermissionCategory;
  ui: UIPattern;
}

export interface RootPermissionConfig extends BasePermissionConfig {
  parentKey?: never;
  children?: Record<string, ChildPermissionConfig>;
}

export interface ChildPermissionConfig extends BasePermissionConfig {
  parentKey: string;
  children?: Record<string, ChildPermissionConfig>;
}

export type PermissionConfig = RootPermissionConfig | ChildPermissionConfig;

/**
 * THE SINGLE SOURCE OF TRUTH FOR ALL PERMISSIONS
 *
 * This configuration object defines every permission in the system.
 * Each permission specifies:
 * - key: Unique identifier (e.g., 'view_documents')
 * - category: Permission category (documents/content_library/settings)
 * - ui: All UI metadata (name, description, type, defaultValue, options)
 * - parentKey: Parent permission key (required for child permissions)
 * - children: Nested child permissions (for hierarchical structures)
 *
 * Hierarchy is represented through nested 'children' objects.
 * The UI generation hook will traverse this config to create the permission tree.
 *
 * To add a new permission, simply add a new entry to this object.
 * All UI rendering is driven by this config - no metadata comes from the backend.
 */
export const PERMISSION_CONFIG = {
  // ============= DOCUMENTS SECTION =============
  view_documents: {
    key: 'view_documents',
    category: PermissionCategory.DOCUMENTS,
    ui: {
      type: 'toggle',
      name: 'View Documents',
      description: 'Allow users to view documents',
      defaultValue: false,
    },
    children: {
      create_document: {
        key: 'create_document',
        category: PermissionCategory.DOCUMENTS,
        parentKey: 'view_documents',
        ui: {
          type: 'toggle',
          name: 'Create Documents',
          description: 'Allow users to create new documents',
          defaultValue: false,
        },
      },

      edit_documents: {
        key: 'edit_documents',
        category: PermissionCategory.DOCUMENTS,
        parentKey: 'view_documents',
        ui: {
          type: 'toggle',
          name: 'Edit documents',
          description: 'Allow users to edit documents they have access to',
          defaultValue: false,
        },
      },
      delete_document: {
        key: 'delete_document',
        category: PermissionCategory.DOCUMENTS,
        parentKey: 'view_documents',
        ui: {
          type: 'toggle',
          name: 'Delete Documents',
          description: 'Allow users to delete documents',
          defaultValue: false,
        },
      },
      duplicate_document: {
        key: 'duplicate_document',
        category: PermissionCategory.DOCUMENTS,
        parentKey: 'view_documents',
        ui: {
          type: 'toggle',
          name: 'Duplicate Documents',
          description: 'Allow users to duplicate documents',
          defaultValue: false,
        },
      },
      share_document: {
        key: 'share_document',
        category: PermissionCategory.DOCUMENTS,
        parentKey: 'view_documents',
        ui: {
          type: 'toggle',
          name: 'Share Documents',
          description: 'Allow users to share documents with external recipients',
          defaultValue: false,
        },
      },
    },
  },

  lock_blocks: {
    key: 'lock_blocks',
    category: PermissionCategory.DOCUMENTS,
    ui: {
      type: 'toggle',
      name: 'Lock and Unlock Blocks on the Canvas',
      description:
        'Allow users to lock and unlock blocks (images, videos, etc.) on the canvas to control who can edit and cannot be edited',
      defaultValue: false,
    },
  },

  document_approval_required: {
    key: 'document_approval_required',
    category: PermissionCategory.DOCUMENTS,
    ui: {
      type: 'toggle',
      name: 'Document Approval Request',
      description: 'Users with this role must request an approval before sharing a document with an external recipient.',
      defaultValue: false,
    },
  },

  // ============= CONTENT LIBRARY SECTION =============
  access_content_library: {
    key: 'access_content_library',
    category: PermissionCategory.CONTENT_LIBRARY,
    ui: {
      type: 'toggle',
      name: 'Access Content Library page',
      description: 'Allow users to access the Content Library page',
      defaultValue: false,
      tooltip: {
        text: 'users_and_permissions.edit_role.access_content_library_tooltip',
        placement: 'right',
      },
    },
    children: {
      view_templates: {
        key: 'view_templates',
        category: PermissionCategory.CONTENT_LIBRARY,
        parentKey: 'access_content_library',
        ui: {
          type: 'toggle',
          name: 'View Templates',
          description: 'Choose which templates users can view',
          defaultValue: false,
        },
        children: {
          create_templates: {
            key: 'create_templates',
            category: PermissionCategory.CONTENT_LIBRARY,
            parentKey: 'view_templates',
            ui: {
              type: 'toggle',
              name: 'Create Templates',
              description: 'Allow users to create templates or save documents as templates',
              defaultValue: false,
            },
          },

          delete_templates: {
            key: 'delete_templates',
            category: PermissionCategory.CONTENT_LIBRARY,
            parentKey: 'view_templates',
            ui: {
              type: 'toggle',
              name: 'Delete Templates',
              description: 'Allow users to delete templates',
              defaultValue: false,
            },
          },

          edit_templates: {
            key: 'edit_templates',
            category: PermissionCategory.CONTENT_LIBRARY,
            parentKey: 'view_templates',
            ui: {
              type: 'toggle',
              name: 'Edit Templates',
              description: 'Allow users to edit templates in the library',
              defaultValue: false,
            },
          },
        },
      },

      view_sections: {
        key: 'view_sections',
        category: PermissionCategory.CONTENT_LIBRARY,
        parentKey: 'access_content_library',
        ui: {
          type: 'toggle',
          name: 'View Sections',
          description: 'Choose which sections users can view',
          defaultValue: false,
        },
        children: {
          create_sections: {
            key: 'create_sections',
            category: PermissionCategory.CONTENT_LIBRARY,
            parentKey: 'view_sections',
            ui: {
              type: 'toggle',
              name: 'Create Sections',
              description: 'Allow users to save sections in the library',
              defaultValue: false,
            },
          },

          delete_sections: {
            key: 'delete_sections',
            category: PermissionCategory.CONTENT_LIBRARY,
            parentKey: 'view_sections',
            ui: {
              type: 'toggle',
              name: 'Delete Sections',
              description: 'Allow users to delete sections from the library',
              defaultValue: false,
            },
          },

          edit_sections: {
            key: 'edit_sections',
            category: PermissionCategory.CONTENT_LIBRARY,
            parentKey: 'view_sections',
            ui: {
              type: 'toggle',
              name: 'Edit Sections',
              description: 'Allow users to edit sections',
              defaultValue: false,
            },
          },
        },
      },

      view_snippets: {
        key: 'view_snippets',
        category: PermissionCategory.CONTENT_LIBRARY,
        parentKey: 'access_content_library',
        ui: {
          type: 'toggle',
          name: 'View Snippets',
          description: 'Choose which snippets users can view',
          defaultValue: true,
        },
        children: {
          create_snippets: {
            key: 'create_snippets',
            category: PermissionCategory.CONTENT_LIBRARY,
            parentKey: 'view_snippets',
            ui: {
              type: 'toggle',
              name: 'Create Snippets',
              description: 'Allow users to create snippets and save them in the library',
              defaultValue: false,
            },
          },

          delete_snippets: {
            key: 'delete_snippets',
            category: PermissionCategory.CONTENT_LIBRARY,
            parentKey: 'view_snippets',
            ui: {
              type: 'toggle',
              name: 'Delete Snippets',
              description: 'Allow users to delete snippets from the library',
              defaultValue: false,
            },
          },

          edit_snippets: {
            key: 'edit_snippets',
            category: PermissionCategory.CONTENT_LIBRARY,
            parentKey: 'view_snippets',
            ui: {
              type: 'toggle',
              name: 'Edit Snippets',
              description: 'Allow users to edit snippets in the library',
              defaultValue: false,
            },
          },
        },
      },

      view_images: {
        key: 'view_images',
        category: PermissionCategory.CONTENT_LIBRARY,
        parentKey: 'access_content_library',
        ui: {
          type: 'toggle',
          name: 'View Images',
          description: 'Choose which images users can view',
          defaultValue: true,
        },
        children: {
          upload_images: {
            key: 'upload_images',
            category: PermissionCategory.CONTENT_LIBRARY,
            parentKey: 'view_images',
            ui: {
              type: 'toggle',
              name: 'Upload Images',
              description: 'Allow users to upload images to the library',
              defaultValue: false,
            },
          },

          delete_images: {
            key: 'delete_images',
            category: PermissionCategory.CONTENT_LIBRARY,
            parentKey: 'view_images',
            ui: {
              type: 'toggle',
              name: 'Delete Images',
              description: 'Allow users to delete images from the library',
              defaultValue: false,
            },
          },
        },
      },
    },
  },

  // ============= USERS & SETTINGS SECTION =============

  access_settings: {
    key: 'access_settings',
    category: PermissionCategory.SETTINGS,
    ui: {
      type: 'toggle',
      name: 'Access Settings Page',
      description: 'Allow users to see the Settings page to manage their workspace',
      defaultValue: false,
    },
    children: {
      access_billing: {
        key: 'access_billing',
        category: PermissionCategory.SETTINGS,
        parentKey: 'access_settings',
        ui: {
          type: 'toggle',
          name: 'Access Billing Page',
          description: 'Allow users to view and manage billing',
          defaultValue: false,
        },
        children: {
          billing_purchase_collaborator_seats: {
            key: 'billing_purchase_collaborator_seats',
            category: PermissionCategory.SETTINGS,
            parentKey: 'access_billing',
            ui: {
              type: 'toggle',
              name: 'Purchase Collaborator Seats (Billing)',
              description: 'Purchase seats directly from billing page',
              defaultValue: false,
            },
          },
        },
      },
      access_users: {
        key: 'access_users',
        category: PermissionCategory.SETTINGS,
        parentKey: 'access_settings',
        ui: {
          type: 'toggle',
          name: 'Access Users Page',
          description: 'Allow users to browse and manage workspace users',
          defaultValue: false,
        },
        children: {
          manage_users: {
            key: 'manage_users',
            category: PermissionCategory.SETTINGS,
            parentKey: 'access_users',
            ui: {
              type: 'toggle',
              name: 'Manage Users',
              description: 'Allow users to manage the entire user base',
              defaultValue: false,
            },
          },
          invite_user: {
            key: 'invite_user',
            category: PermissionCategory.SETTINGS,
            parentKey: 'access_users',
            ui: {
              type: 'toggle',
              name: 'Invite User',
              description: 'Allow users to invite new users to the workspace',
              defaultValue: false,
            },
          },
          edit_user: {
            key: 'edit_user',
            category: PermissionCategory.SETTINGS,
            parentKey: 'access_users',
            ui: {
              type: 'toggle',
              name: 'Edit User',
              description: 'Allow users to edit other users information',
              defaultValue: false,
            },
          },
          remove_user: {
            key: 'remove_user',
            category: PermissionCategory.SETTINGS,
            parentKey: 'access_users',
            ui: {
              type: 'toggle',
              name: 'Remove User',
              description: 'Allow users to remove users from the workspace',
              defaultValue: false,
            },
          },
          manage_user_groups: {
            key: 'manage_user_groups',
            category: PermissionCategory.SETTINGS,
            parentKey: 'access_users',
            ui: {
              type: 'toggle',
              name: 'Manage User Groups',
              description: 'Allow users to manage user groups',
              defaultValue: false,
            },
          },
          purchase_seats: {
            key: 'purchase_seats',
            category: PermissionCategory.SETTINGS,
            parentKey: 'access_users',
            ui: {
              type: 'toggle',
              name: 'Purchase User Seats',
              description: 'Allow users to purchase additional user seats',
              defaultValue: false,
            },
          },
          purchase_collaborator_seats: {
            key: 'purchase_collaborator_seats',
            category: PermissionCategory.SETTINGS,
            parentKey: 'access_users',
            ui: {
              type: 'toggle',
              name: 'Purchase Collaborator Seats',
              description: 'Allow users to purchase collaborator seats',
              defaultValue: false,
            },
          },
          manage_roles_permissions: {
            key: 'manage_roles_permissions',
            category: PermissionCategory.SETTINGS,
            parentKey: 'access_users',
            ui: {
              type: 'toggle',
              name: 'Manage Roles and Permissions',
              description: 'Allow users to manage roles, edit and create custom ones',
              defaultValue: false,
            },
            children: {
              upsert_role: {
                key: 'upsert_role',
                category: PermissionCategory.SETTINGS,
                parentKey: 'manage_roles_permissions',
                ui: {
                  type: 'toggle',
                  name: 'Edit Roles',
                  description: 'Allow users to create or update roles',
                  defaultValue: false,
                },
              },
              delete_role: {
                key: 'delete_role',
                category: PermissionCategory.SETTINGS,
                parentKey: 'manage_roles_permissions',
                ui: {
                  type: 'toggle',
                  name: 'Delete Roles',
                  description: 'Allow users to delete roles',
                  defaultValue: false,
                },
              },
              assign_role: {
                key: 'assign_role',
                category: PermissionCategory.SETTINGS,
                parentKey: 'manage_roles_permissions',
                ui: {
                  type: 'toggle',
                  name: 'Assign Role',
                  description: 'Allow users to assign roles to other users',
                  defaultValue: false,
                },
              },
            },
          },
        },
      },
      custom_fields_management: {
        key: 'custom_fields_management',
        category: PermissionCategory.SETTINGS,
        parentKey: 'access_settings',
        ui: {
          type: 'toggle',
          name: 'Access Custom Fields',
          description: 'Allow users to manage custom fields',
          defaultValue: false,
        },
      },
      access_connect_api: {
        key: 'access_connect_api',
        category: PermissionCategory.SETTINGS,
        parentKey: 'access_settings',
        ui: {
          type: 'toggle',
          name: 'Access Connect API Page',
          description: 'Allow users to manage API clients and access the Connect API page',
          defaultValue: false,
        },
      },
      access_integrations: {
        key: 'access_integrations',
        category: PermissionCategory.SETTINGS,
        parentKey: 'access_settings',
        ui: {
          type: 'toggle',
          name: 'Access Integrations page',
          description: 'Allow users to manage integrations',
          defaultValue: false,
        },
      },
    },
  },

  access_email_templates: {
    key: 'access_email_templates',
    category: PermissionCategory.SETTINGS,
    ui: {
      type: 'toggle',
      name: 'Access Email Templates page',
      description: 'Allow users to manage email templates',
      defaultValue: true,
    },
  },
  access_automations: {
    key: 'access_automations',
    category: PermissionCategory.AUTOMATIONS,
    ui: {
      type: 'toggle',
      name: 'Access Automations Page',
      description: 'Allow users to access the Automations page',
      defaultValue: true,
    },
  },
} as const satisfies Record<string, PermissionConfig>;

/**
 * Type representing all valid permission keys
 * Auto-derived from PERMISSION_CONFIG for type safety
 */
export type PermissionKey = keyof typeof PERMISSION_CONFIG;
